<template>
  <div class="callback-container">
    <div class="spinner"></div>
    <p>{{ message }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../store/auth'
import { householdAPI, syncAPI, forwardingAPI } from '../services/api'

const router = useRouter()
const authStore = useAuthStore()
const message = ref('Completing authentication...')

onMounted(async () => {
  try {
    // Get token from URL
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')

    if (!token) {
      message.value = 'Authentication failed: No token received'
      setTimeout(() => router.push('/login'), 2000)
      return
    }

    // Handle the callback
    await authStore.handleCallback(token)

    // Check for pending invite token from localStorage
    const pendingInviteToken = localStorage.getItem('pendingInviteToken')
    if (pendingInviteToken) {
      message.value = 'Joining household...'
      localStorage.removeItem('pendingInviteToken')

      try {
        await householdAPI.acceptInvite(pendingInviteToken)
        message.value = 'Successfully joined household! Redirecting...'
        setTimeout(() => router.push('/settings'), 1000)
        return
      } catch (inviteError) {
        // If invite fails (expired, already used, etc.), continue to dashboard
        console.error('Failed to accept invite:', inviteError)
        message.value = 'Could not join household. Redirecting to dashboard...'
        setTimeout(() => router.push('/dashboard'), 2000)
        return
      }
    }

    // Check if onboarding is needed
    const onboardingComplete = localStorage.getItem('onboardingComplete')
    if (!onboardingComplete) {
      // Check if setup is already done (Monarch + forwarding)
      try {
        const [monarchRes, forwardingRes] = await Promise.all([
          syncAPI.getMonarchStatus(),
          forwardingAPI.getStatus()
        ])
        const monarchConnected = monarchRes.data.connected
        const forwardingVerified = forwardingRes.data.forwarding_verified

        if (monarchConnected && forwardingVerified) {
          // Setup already complete, skip onboarding
          localStorage.setItem('onboardingComplete', 'true')
          message.value = 'Authentication successful! Redirecting...'
          setTimeout(() => router.push('/dashboard'), 1000)
          return
        }
      } catch (err) {
        console.error('Error checking setup status:', err)
      }

      message.value = 'Authentication successful! Starting setup...'
      setTimeout(() => router.push('/onboarding'), 1000)
    } else {
      message.value = 'Authentication successful! Redirecting...'
      setTimeout(() => router.push('/dashboard'), 1000)
    }
  } catch (error) {
    message.value = 'Authentication failed: ' + (error.message || 'Unknown error')
    setTimeout(() => router.push('/login'), 2000)
  }
})
</script>

<style scoped>
.callback-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f5f5;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

p {
  color: #555;
  font-size: 1.1rem;
}
</style>
